/**
 * RequireRole — client-side route guard for role-gated screens.
 *
 * This is UX gating ONLY (hiding the manager/admin views from staff). Real
 * authorization is enforced server-side (JWT → profiles → role) and by RLS.
 * Never rely on this component for security.
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useSimulation } from "@/auth/SimulationProvider";
import type { AppRole } from "@/types";
import { LoadingState } from "@/components/LoadingState";

interface RequireRoleProps {
  /** Roles allowed to view the children. */
  allow: AppRole[];
  children: ReactNode;
  /** Where to send users who lack the role (default: call list). */
  redirectTo?: string;
}

export function RequireRole({ allow, children, redirectTo = "/calls" }: RequireRoleProps) {
  const { session, initializing } = useAuth();
  const { effectiveRole: role } = useSimulation();

  if (initializing) return <LoadingState label="Checking access…" />;

  // Not signed in → the app router already handles this, but guard anyway.
  if (!session) return <Navigate to="/login" replace />;

  if (!role || !allow.includes(role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
